import type {Component, Editor} from 'grapesjs';
import {EMAIL_MAKER_COMPONENT_TYPES} from '@EmailMakerReact/email-maker-react.constants';
import {getBooleanProp, getMainWrapper, getStringAttributes} from '@EmailMakerReact/lib/email-maker-component-query-library';

/** Selection, canvas scrolling, and editor refresh for the React email maker. */

export const SCROLL_TO_REVEALED_COMPONENT_DELAY_MS = 300;

const REVEAL_INVALID_LINK_EVENT = 'email-maker:reveal-invalid-link';

interface RevealInvalidLinkEventDetail {
  href: string;
}

export interface ScrollToComponentOptions {
  /** Falls back to the current selection when omitted. */
  component?: Component;
  delay?: number;
  scrollOpts?: {
    behavior?: ScrollBehavior;
    block?: ScrollLogicalPosition;
    force?: boolean;
  };
}

/** Selects the PMW wrapper found by walking first children from the root. */
export function setDefaultSelection(editor: Editor): void {
  editor.select(getPmwWrapperByFirstChildWalk(editor.getWrapper() ?? undefined));
}

/** Selects the next sibling, wrapping around. */
export function selectNextItem(editor: Editor): void {
  const selected = editor.getSelected();
  const collection = selected?.collection;

  if (!selected || !collection || collection.length === 0) {
    return;
  }

  const index = (collection.indexOf(selected) + 1) % collection.length;

  const nextModel = collection.at(index);
  if (nextModel) {
    editor.select(nextModel);
  }
}

/** Selects the previous sibling, wrapping around. */
export function selectPreviousItem(editor: Editor): void {
  const selected = editor.getSelected();
  const collection = selected?.collection;

  if (!selected || !collection || collection.length === 0) {
    return;
  }

  const index = (collection.indexOf(selected) - 1 + collection.length) % collection.length;

  const previousModel = collection.at(index);
  if (previousModel) {
    editor.select(previousModel);
  }
}

/** "Deselects" by selecting the main wrapper. */
export function unselectComponent(editor: Editor): void {
  editor.select(getMainWrapper(editor));
}

/** Smooth-scrolls the canvas to the component after an optional delay. */
export function scrollToComponent(editor: Editor, options: ScrollToComponentOptions = {}): void {
  const component = options.component ?? editor.getSelected();
  if (!component) {
    return;
  }

  const scrollOpts = {
    block: options.scrollOpts?.block ?? 'center',
    behavior: options.scrollOpts?.behavior ?? 'smooth',
    force: options.scrollOpts?.force ?? true,
  };

  setTimeout(() => {
    // The editor can be destroyed before this delayed scroll fires; bail instead of touching a dead canvas.
    if (editor.getModel().destroyed) {
      return;
    }
    editor.Canvas.scrollTo(component, scrollOpts);
  }, options.delay ?? 0);
}

/** Deselects and reselects the component so selection listeners re-run. */
export function refreshSelection(editor: Editor): void {
  const selected = editor.getSelected();

  if (!selected) {
    return;
  }

  editor.selectRemove(selected);
  editor.select(selected);
}

/** Recalculates canvas offsets, optionally after a delay. */
export function refreshEditor(editor: Editor, delay?: number): void {
  if (typeof delay === 'undefined') {
    editor.refresh();
    return;
  }

  setTimeout(() => {
    // The editor can be destroyed before this delayed refresh fires; bail instead of touching a dead canvas.
    if (editor.getModel().destroyed) {
      return;
    }
    editor.refresh();
  }, delay);
}

/** Unlike getMainWrapper's find(), walks first children from the root until the PMW wrapper is hit. */
function getPmwWrapperByFirstChildWalk(rootComponent: Component | undefined): Component | undefined {
  if (!rootComponent) {
    return rootComponent;
  }

  return rootComponent.is(EMAIL_MAKER_COMPONENT_TYPES.container.PMW_WRAPPER) ? rootComponent : getPmwWrapperByFirstChildWalk(rootComponent.getChildAt(0));
}

/**
 * Selects the nearest selectable ancestor of `component` and scrolls it into view. A starting
 * component flagged `selectable: false` (such as a PMW link) is a no-op for editor.select(), so we
 * climb to the first selectable node before selecting. Generic: usable for any "jump to a component" need.
 */
export function selectAndRevealComponent(editor: Editor, component: Component): void {
  const target = getNearestSelectableComponent(component);
  if (!target) {
    return;
  }

  editor.select(target);
  scrollToComponent(editor, {component: target, delay: SCROLL_TO_REVEALED_COMPONENT_DELAY_MS});
}

/** Climbs from `component` to the first ancestor that is selectable, stopping at the wrapper. */
function getNearestSelectableComponent(component: Component): Component | undefined {
  let current: Component | undefined = component;

  while (current && !getBooleanProp(current, 'selectable')) {
    current = current.parent();
  }

  return current;
}

/** Finds the PMW link component whose href attribute exactly matches `href`. */
export function findLinkComponentByHref(editor: Editor, href: string): Component | undefined {
  const links = editor.getWrapper()?.findType(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_LINK) ?? [];
  return links.find((link) => getStringAttributes(link).href === href);
}

/** Broadcasts a request to reveal the text component holding a given bad link, crossing the React context boundary via the DOM. */
export function dispatchRevealInvalidLink(href: string): void {
  document.dispatchEvent(new CustomEvent<RevealInvalidLinkEventDetail>(REVEAL_INVALID_LINK_EVENT, {detail: {href}}));
}

/** Subscribes to reveal-invalid-link requests; returns a cleanup that removes the listener. */
export function subscribeToRevealInvalidLink(handler: (href: string) => void): () => void {
  const listener = (event: Event): void => {
    const href = getRevealInvalidLinkHref(event);
    if (href !== undefined) {
      handler(href);
    }
  };

  document.addEventListener(REVEAL_INVALID_LINK_EVENT, listener);

  return () => {
    document.removeEventListener(REVEAL_INVALID_LINK_EVENT, listener);
  };
}

/** The single validated boundary that reads the CustomEvent detail off a loosely-typed DOM Event. */
function getRevealInvalidLinkHref(event: Event): string | undefined {
  const detail: unknown = event instanceof CustomEvent ? event.detail : undefined;
  if (detail === null || typeof detail !== 'object' || !('href' in detail)) {
    return undefined;
  }

  const href = (detail as RevealInvalidLinkEventDetail).href;
  return typeof href === 'string' ? href : undefined;
}
